import { useCallback, useMemo, useState } from "react";
import { OriQaConnectionParams } from "apps/user/learner/hooks/use-ori-connection";

// The bounded thread currently open in the chat view.
export interface ActiveQaThread {
  threadId: string;
  conversationId: string;
}

export interface UseOriQaSessionReturn {
  // The top-level Chat/QA context switch (only surfaced under the ori_qa flag).
  qaMode: boolean;
  setQaMode: (on: boolean) => void;
  selectedConfigId: string | null;
  setSelectedConfigId: (id: string | null) => void;
  // The bounded thread open in the chat, or null when awaiting a thread
  // (nothing to connect to yet).
  activeThread: ActiveQaThread | null;
  // Open a thread in the chat — a freshly created one, or a resumed past one.
  openThread: (thread: ActiveQaThread) => void;
  // Close the open thread (e.g. after archiving it), returning to the
  // awaiting-thread state.
  closeThread: () => void;
  // Bounded-thread connection params — non-null only in QA mode with a config
  // chosen AND a thread open. Writes are always off for a bounded thread, so
  // there is no write flag here; the ticket never carries one.
  connectionParams: OriQaConnectionParams | null;
}

// Page-scoped QA state: the mode switch, the config under test, and the open
// bounded thread. Plain React state — no persistence across navigations.
// Selecting a config is decoupled from opening a thread: you flip to QA,
// choose a config, then start a new thread or resume a past one in the QA
// drawer, at which point the bounded connection comes up.
export default function useOriQaSession(): UseOriQaSessionReturn {
  const [qaMode, setQaModeState] = useState(false);
  const [selectedConfigId, setSelectedConfigIdState] = useState<string | null>(
    null
  );
  const [activeThread, setActiveThread] = useState<ActiveQaThread | null>(null);

  const setQaMode = useCallback((on: boolean) => {
    setQaModeState(on);
    // Leaving QA drops any open thread so the socket tears down; the config
    // choice is cleared too so re-entering starts clean.
    if (!on) {
      setActiveThread(null);
      setSelectedConfigIdState(null);
    }
  }, []);

  const setSelectedConfigId = useCallback((id: string | null) => {
    setSelectedConfigIdState(id);
    // A thread belongs to one configuration — switching configs abandons the
    // open thread (a new one is started against the new config).
    setActiveThread(null);
  }, []);

  const openThread = useCallback((thread: ActiveQaThread) => {
    setActiveThread(thread);
  }, []);

  const closeThread = useCallback(() => {
    setActiveThread(null);
  }, []);

  const connectionParams = useMemo<OriQaConnectionParams | null>(
    () =>
      qaMode && selectedConfigId && activeThread
        ? {
            configId: selectedConfigId,
            threadId: activeThread.threadId,
            conversationId: activeThread.conversationId,
          }
        : null,
    [qaMode, selectedConfigId, activeThread]
  );

  return {
    qaMode,
    setQaMode,
    selectedConfigId,
    setSelectedConfigId,
    activeThread,
    openThread,
    closeThread,
    connectionParams,
  };
}
